import type { Language } from 'codegen/types'
import { defaultLanguage } from 'cf-i18n/constants'
import { getStudioLanguageFromPathname } from '@repo/react-router-locale/canonicalize-studio-locale-pathname'

interface ResolveStudioLanguageForUrlInput {
  pathname: string
  accountLanguage: Language | null | undefined
  cookieLanguage: Language | null
  isLoggedIn: boolean | undefined
  isAuthResolved: boolean
  /** Account language fetch finished (success or error), not only success. */
  isAccountLanguageSettled: boolean
}

export interface StudioLanguageForUrlQueryResult {
  data: Language
  isSuccess: boolean
}

function getPreferredStudioLanguage({
  accountLanguage,
  cookieLanguage,
  isLoggedIn,
  isAuthResolved,
  isAccountLanguageSettled,
}: Omit<ResolveStudioLanguageForUrlInput, 'pathname'>): Language | undefined {
  if (!isAuthResolved || !isLoggedIn) {
    return cookieLanguage ?? undefined
  }

  if (!isAccountLanguageSettled) {
    return cookieLanguage ?? undefined
  }

  return accountLanguage ?? cookieLanguage ?? undefined
}

export function resolveStudioLanguageForUrl({
  pathname,
  accountLanguage,
  cookieLanguage,
  isLoggedIn,
  isAuthResolved,
  isAccountLanguageSettled,
}: ResolveStudioLanguageForUrlInput): StudioLanguageForUrlQueryResult {
  const urlLanguage = getStudioLanguageFromPathname(pathname)
  const preferredLanguage = getPreferredStudioLanguage({
    accountLanguage,
    cookieLanguage,
    isLoggedIn,
    isAuthResolved,
    isAccountLanguageSettled,
  })

  if (urlLanguage) {
    // First visit: no stored preference yet, so the URL locale seeds language + cookie.
    // After that, cookie/account preference wins and `useSyncLocaleUrl` rewrites the prefix.
    return { data: preferredLanguage ?? urlLanguage, isSuccess: true }
  }

  if (!isAuthResolved) {
    return { data: cookieLanguage ?? defaultLanguage, isSuccess: false }
  }

  if (!isLoggedIn) {
    return { data: cookieLanguage ?? defaultLanguage, isSuccess: true }
  }

  return {
    data: accountLanguage ?? cookieLanguage ?? defaultLanguage,
    isSuccess: isAccountLanguageSettled,
  }
}

/** Whether `useSyncLocaleUrl` may rewrite the current pathname. */
export function isStudioLanguageForUrlSyncEnabled({
  isSuccess,
  urlSyncReady,
}: {
  isSuccess: boolean
  urlSyncReady: boolean
}): boolean {
  // Explicit URL locales resolve immediately (`isSuccess`), while unprefixed entry
  // URLs still sync from cookie language while auth is pending (`urlSyncReady`).
  return isSuccess || urlSyncReady
}
